import type { PlayerEvent, WatchProgress } from '../types';
import { saveWatchProgress, getWatchProgress, addToHistory } from './storage';

// Streaming sources from FMHY - ordered by reliability
export const STREAMING_SOURCES = {
  vidsrc: {
    name: 'VidSrc',
    movie: (id: number) => `https://vidsrc.xyz/embed/movie/${id}`,
    tv: (id: number, s: number, e: number) => `https://vidsrc.xyz/embed/tv/${id}/${s}/${e}`,
  },
  vidsrcPro: {
    name: 'VidSrc Pro',
    movie: (id: number) => `https://vidsrc.pro/embed/movie/${id}`,
    tv: (id: number, s: number, e: number) => `https://vidsrc.pro/embed/tv/${id}/${s}/${e}`,
  },
  twoembed: {
    name: '2Embed',
    movie: (id: number) => `https://www.2embed.cc/embed/${id}`,
    tv: (id: number, s: number, e: number) => `https://www.2embed.cc/embedtv/${id}&s=${s}&e=${e}`,
  },
  vidlink: {
    name: 'VidLink',
    movie: (id: number) => `https://vidlink.pro/movie/${id}`,
    tv: (id: number, s: number, e: number) => `https://vidlink.pro/tv/${id}/${s}/${e}`,
  },
  embedsu: {
    name: 'Embed.su',
    movie: (id: number) => `https://embed.su/embed/movie/${id}`,
    tv: (id: number, s: number, e: number) => `https://embed.su/embed/tv/${id}/${s}/${e}`,
  },
} as const;

export type StreamingSource = keyof typeof STREAMING_SOURCES;

const VIDKING_BASE = import.meta.env.VITE_VIDKING_BASE || 'https://www.vidking.net/embed';

export interface PlayerOptions {
  color?: string;
  autoPlay?: boolean;
  nextEpisode?: boolean;
  episodeSelector?: boolean;
  progress?: number;
}

// Build Vidking embed URL
export const buildPlayerUrl = (
  type: 'movie' | 'tv',
  tmdbId: number,
  options: PlayerOptions = {},
  season?: number,
  episode?: number
): string => {
  let url = type === 'movie'
    ? `${VIDKING_BASE}/movie/${tmdbId}`
    : `${VIDKING_BASE}/tv/${tmdbId}/${season}/${episode}`;

  const params = new URLSearchParams();

  if (options.color) params.set('color', options.color.replace('#', ''));
  if (options.autoPlay !== undefined) params.set('autoPlay', String(options.autoPlay));
  if (type === 'tv') {
    if (options.nextEpisode !== undefined) params.set('nextEpisode', String(options.nextEpisode));
    if (options.episodeSelector !== undefined) params.set('episodeSelector', String(options.episodeSelector));
  }
  if (options.progress) params.set('progress', String(Math.floor(options.progress)));

  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
};

// Build URL from any streaming source
export const getSourceUrl = (
  source: StreamingSource,
  type: 'movie' | 'tv',
  tmdbId: number,
  season?: number,
  episode?: number
): string => {
  const src = STREAMING_SOURCES[source];
  if (type === 'movie') {
    return src.movie(tmdbId);
  }
  return src.tv(tmdbId, season ?? 1, episode ?? 1);
};

// Get all available source URLs for fallback
export const getAllSourceUrls = (
  type: 'movie' | 'tv',
  tmdbId: number,
  season?: number,
  episode?: number
): { name: string; url: string; key: StreamingSource }[] => {
  return Object.entries(STREAMING_SOURCES).map(([key, src]) => ({
    key: key as StreamingSource,
    name: src.name,
    url: type === 'movie'
      ? src.movie(tmdbId)
      : src.tv(tmdbId, season ?? 1, episode ?? 1),
  }));
};

// Parse player events from postMessage.
// Strictly validates Vidking's PLAYER_EVENT shape. Some other providers (e.g.
// VidFast) send the SAME `type:'PLAYER_EVENT'` envelope but with different inner
// fields (player_status / player_progress instead of event / currentTime /
// progress). Accepting those would persist NaN/undefined Continue-Watching
// entries, so we require the exact fields createProgressHandler reads.
const KNOWN_PLAYER_EVENTS = ['timeupdate', 'play', 'pause', 'ended', 'seeked'];

export const parsePlayerEvent = (eventData: unknown): PlayerEvent | null => {
  try {
    if (typeof eventData !== 'string') return null;
    const parsed = JSON.parse(eventData);
    if (
      parsed?.type === 'PLAYER_EVENT' &&
      parsed.data &&
      typeof parsed.data.event === 'string' &&
      KNOWN_PLAYER_EVENTS.includes(parsed.data.event) &&
      typeof parsed.data.currentTime === 'number' &&
      typeof parsed.data.progress === 'number'
    ) {
      return parsed as PlayerEvent;
    }
    return null;
  } catch {
    return null;
  }
};

// Create progress handler
export const createProgressHandler = (
  mediaType: 'movie' | 'tv',
  mediaId: number,
  title: string,
  posterPath: string | null,
  season?: number,
  episode?: number
) => {
  let lastSaveTime = 0;
  const SAVE_INTERVAL = 10000; // Save every 10 seconds max

  return (event: PlayerEvent) => {
    const now = Date.now();

    // Save progress periodically
    if (now - lastSaveTime > SAVE_INTERVAL || event.data.event === 'pause' || event.data.event === 'ended') {
      lastSaveTime = now;

      const progress: WatchProgress = {
        mediaId,
        mediaType,
        progress: event.data.progress,
        currentTime: event.data.currentTime,
        duration: event.data.duration,
        season,
        episode,
        updatedAt: now,
        title,
        posterPath,
      };

      saveWatchProgress(progress);

      // Add to history when video starts
      if (event.data.event === 'play' && event.data.progress < 5) {
        addToHistory({
          id: mediaId,
          mediaType,
          title,
          posterPath,
          backdropPath: null,
          voteAverage: 0,
          releaseDate: '',
          addedAt: now,
        });
      }
    }
  };
};

// Get saved progress for resuming
export const getSavedProgress = (
  mediaType: 'movie' | 'tv',
  mediaId: number,
  season?: number,
  episode?: number
): number => {
  const progress = getWatchProgress(mediaType, mediaId, season, episode);
  // Only resume if between 5% and 95%
  if (progress && progress.progress > 5 && progress.progress < 95) {
    return progress.currentTime;
  }
  return 0;
};

// Theme colors that match Vidking presets
export const PLAYER_THEMES = {
  netflix: 'e50914',
  youtube: 'ff0000',
  twitch: '9146ff',
  discord: '5865f2',
  spotify: '1db954',
  blue: '0dcaf0',
  orange: 'ff7b00',
  pink: 'e91e63',
} as const;

export type PlayerTheme = keyof typeof PLAYER_THEMES;
